import React from 'react';
import { useLazyLoadQuery } from 'react-relay/hooks';

import Grid from '@material-ui/core/Grid';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { graphql } from 'babel-plugin-relay/macro';
import { getFirstActiveContact } from 'ui-utils';

import {
  ActionsPatient,
  ActionsPatientProps,
  CardPatient,
  Chat,
  ChatProps,
  HubPatientJourney,
  ToolbarNavigationPatient,
  ToolbarNavigationPatientProps,
} from 'deprecated/fragments';

import Contact from 'components/Contact';

import { QueryChatQuery } from './__generated__/QueryChatQuery.graphql';
import WrapperQueryPatientAuditContactTooltip from './WrapperQueryPatientAuditContactTooltip';

const styles = ({
  custom: {
    palette: { colorSupportSnow },
  },
}: Theme) =>
  createStyles({
    chat: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      justifyContent: 'flex-end',
      background: colorSupportSnow,
    },
    hubContentWrapper: {
      display: 'flex',
      height: '100%',
    },
    hubPatientJourneyWrap: {
      height: '100%',
      overflow: 'auto',
    },
  });

export interface QueryChatProps
  extends WithStyles<typeof styles>,
    Omit<ChatProps, 'classes' | 'fragmentRefViewer' | 'fragmentRef'> {
  patientId: string;
  showPatientJourney?: boolean;
  onPatientHeaderAction?: ActionsPatientProps['onAction'];
  onBack?: ToolbarNavigationPatientProps['onBack'];
  onClose?: ToolbarNavigationPatientProps['onClose'];
}

const QueryChat: React.FC<QueryChatProps> = ({
  classes,
  variant,
  patientId,
  showPatientJourney = false,
  onPatientHeaderAction = () => {},
  onBack,
  onClose,
  ...rest
}) => {
  const { viewer, patientById: patient } = useLazyLoadQuery<QueryChatQuery>(
    graphql`
      query QueryChatQuery(
        $id: MongoID!
        $includeActions: Boolean!
        $includePatientJourney: Boolean!
        $includeToolbarNavigationPatient: Boolean!
      ) {
        viewer {
          ...ActionsPatient_Viewer @include(if: $includeActions)
          ...Chat_Viewer
        }
        patientById(_id: $id) {
          _id
          doNotContact
          contact {
            active
            type
            value
            archived
          }
          ...CardPatient_Patient @include(if: $includeActions)
          ...ActionsPatient_Patient @include(if: $includeActions)
          ...ToolbarNavigationPatient_Patient @include(if: $includeToolbarNavigationPatient)
          ...Chat_Patient
          ...HubPatientJourney_Patient @include(if: $includePatientJourney)
        }
      }
    `,
    {
      id: patientId,
      includePatientJourney: showPatientJourney,
      includeActions: variant === 'regular',
      includeToolbarNavigationPatient: variant === 'popper',
    },
  );

  if (!viewer || !patient) {
    return null;
  }

  const firstActiveContact = getFirstActiveContact(patient);

  return (
    <>
      {variant === 'regular' && (
        <CardPatient
          fragmentRef={patient}
          CardHeaderAction={
            <ActionsPatient
              fragmentRefViewer={viewer}
              fragmentRef={patient}
              actions={['reassignChatActivity', 'journey', 'telehealth']}
              moreActions={['goToPatient', 'closeChatActivity']}
              includePatientJourney={showPatientJourney}
              onAction={onPatientHeaderAction}
            />
          }
          CardHeaderPatientContact={
            <>
              <Contact contact={firstActiveContact} />
              <WrapperQueryPatientAuditContactTooltip patientId={patient._id} />
            </>
          }
          includePatientMerge
        />
      )}
      {variant === 'popper' && (
        <ToolbarNavigationPatient
          fragmentRef={patient}
          variant="dense"
          color="supportSlate"
          onBack={onBack}
          onClose={onClose}
        />
      )}
      <Grid container className={classes.hubContentWrapper}>
        <Grid item xs={showPatientJourney ? 6 : 12}>
          <Chat
            className={classes.chat}
            fragmentRefViewer={viewer}
            fragmentRef={patient}
            variant={variant}
            includeActionsChatActivity
            {...rest}
          />
        </Grid>
        {showPatientJourney && (
          <Grid item xs={6} className={classes.hubPatientJourneyWrap}>
            <HubPatientJourney fragmentRef={patient} key={patientId} />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default withStyles(styles)(QueryChat);
